import React, { useEffect, useState } from 'react';
import { AutocompleteRenderInputParams, MenuItem, PaginatedSearchDropdown } from '@hopskipdrive/react-components';
import { Close } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { isArray } from 'lodash';

import { useLocationsQuery } from '../../../locations/hooks/useLocationsQuery';

import { CustomListContainer, StyledFormInputText, TagsContainer } from './SelectAccountLocation.styles';

export const SelectAccountLocation: React.FC<{
  onChange: (location: AccountLocation | null) => void;
  defaultValue?: { id: number | string; name: string } | Array<{ id: number | string; name: string }>;
  placeholder?: string;
  multiple?: boolean;
  id?: string;
  error?: boolean;
  helperText?: string;
  disabledItems?: AccountLocation[];
}> = ({ onChange, placeholder, multiple, id, error, helperText, defaultValue, disabledItems }) => {
  const { debounceHandleQuery, isLoading, locations, handlePagination } = useLocationsQuery(undefined, {
    paginationMode: 'infinite',
  });
  const [locationInputValue, setLocationInputValue] = useState<string | null>(null);
  const [value, setValue] = useState<
    | { label: string; id: number | string; address: string; name: string }
    | Array<{ label: string; id: number | string; address: string; name: string }>
    | null
  >(multiple ? [] : null);
  const [page, setPage] = useState<number>(1);

  const getLabel = (location: AccountLocation | undefined) => {
    if (location) {
      return location.name ? `${location.name}, ${location.address}` : location.address;
    }

    return null;
  };

  useEffect(() => {
    if (multiple && isArray(defaultValue) && defaultValue.length && isArray(value) && !value.length) {
      const mappedLocations = defaultValue.map((loc) => ({
        label: loc?.name,
        id: loc?.id,
        address: '',
        name: loc?.name,
      }));

      setValue(mappedLocations);
      onChange(mappedLocations as unknown as AccountLocation);
    } else if (defaultValue && !isArray(defaultValue) && !value) {
      const mappedLocation = {
        label: defaultValue?.name,
        id: defaultValue?.id,
        address: '',
        name: defaultValue?.name,
      };

      setValue(mappedLocation);
      onChange(mappedLocation as unknown as AccountLocation);
    }
  }, [defaultValue]);

  return (
    <PaginatedSearchDropdown
      id={id || 'select-location'}
      data-testid={id || 'select-location'}
      multiple={multiple}
      hidden={false}
      options={locations.map((loc) => ({
        label: getLabel(loc) || '',
        id: loc.id,
        address: loc.address,
        name: loc.name,
      }))}
      autoHighlight
      freeSolo={true}
      perPage={10}
      pageNumber={page}
      isLoading={isLoading}
      loadNextPage={async () => {
        handlePagination();

        setPage((prevPage) => prevPage + 1);
      }}
      inputValue={locationInputValue || ''}
      setInputValue={(location) => {
        debounceHandleQuery('', { force: true });
        setPage(1);
        setLocationInputValue(location);
      }}
      value={value}
      onChange={(_, location) => {
        debounceHandleQuery('', { force: true });
        setValue(location);
        setPage(1);
        onChange(location);

        if (!location) {
          return '';
        }

        return location.id;
      }}
      boldCharacterMatch={true}
      ListboxComponent={CustomListContainer}
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={option.label}
            disabled={
              !!disabledItems?.find((item) => item.id === option.id) ||
              (multiple && isArray(value) && !!value?.find((item) => item.id === option.id))
            }
          >
            {option.label}
          </MenuItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <StyledFormInputText
          {...params}
          variant="outlined"
          fullWidth
          placeholder={multiple && isArray(value) && value.length > 0 ? '' : placeholder || 'Location'}
          onChange={(e) => {
            setPage(1);
            debounceHandleQuery(e.target.value, { force: true });
          }}
          data-testid="select-location-input"
          error={error}
          helperText={helperText}
        />
      )}
      renderTags={(tagValues, getTagProps) => {
        return (
          <TagsContainer>
            {tagValues.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                label={option.name || option.address?.toString().split(',')[0]}
                key={option.label}
                size="small"
                deleteIcon={<Close />}
              />
            ))}
          </TagsContainer>
        );
      }}
    />
  );
};
